import type {
  AdyenCheckoutClient,
  AdyenCheckoutInstance,
  AdyenComponent,
  AdyenComponentConstructor,
  MountAdyenComponentParams,
} from '@/scopes/payment/infrastructure/adyen/types'
import type { AdyenPaymentAction } from '@/scopes/payment/infrastructure/types'
import { UNSUPPORTED_ADYEN_COMPONENT_TYPE_ERROR_NAME } from '@/scopes/payment/domain/adyen/policies/adyenTelemetry'

type AdyenCheckoutLoader = () => Promise<typeof import('./adyenCheckoutModule')>

interface Dependencies {
  loadAdyenCheckout: AdyenCheckoutLoader
}

const defaultLoadAdyenCheckout: AdyenCheckoutLoader = () => import('./adyenCheckoutModule')

export function createAdyenCheckoutClient(dependencies: Partial<Dependencies> = {}): AdyenCheckoutClient {
  const { loadAdyenCheckout = defaultLoadAdyenCheckout } = dependencies

  let component: AdyenComponent | null = null

  async function mount({
    element,
    config,
    componentType,
    configKey,
    onSubmit,
    onAdditionalDetails,
    onPaymentCompleted,
    onPaymentFailed,
    onError,
    onActionHandled,
    onFieldValid,
  }: MountAdyenComponentParams) {
    const adyenModule = await loadAdyenCheckout()
    const checkout = await adyenModule.AdyenCheckout(config) as unknown as AdyenCheckoutInstance
    const Component = checkout.getComponent(componentType) as unknown as AdyenComponentConstructor | undefined

    if (!Component) {
      const error = new Error(`Unsupported Adyen component type: ${componentType}`)
      error.name = UNSUPPORTED_ADYEN_COMPONENT_TYPE_ERROR_NAME
      throw error
    }

    const paymentMethodsConfig = config.paymentMethodsConfiguration as Record<string, Record<string, unknown>> | undefined
    const componentConfig = paymentMethodsConfig?.[configKey ?? componentType] ?? {}

    component = new Component(checkout, {
      ...componentConfig,
      type: componentType,
      onSubmit: (state, componentInstance, actions) => onSubmit(state, componentInstance, actions),
      onAdditionalDetails: (state, componentInstance, actions) => onAdditionalDetails(state, componentInstance, actions),
      ...(onPaymentCompleted && { onPaymentCompleted }),
      ...(onPaymentFailed && { onPaymentFailed }),
      ...(onError && { onError }),
      ...(onActionHandled && { onActionHandled }),
      ...(onFieldValid && { onFieldValid }),
    })

    component.mount(element)
  }

  function submit() {
    component?.submit()
  }

  function handleAction(action: AdyenPaymentAction) {
    component?.handleAction(action)
  }

  function remove() {
    component?.remove()
    component = null
  }

  return {
    mount,
    submit,
    handleAction,
    remove,
  }
}
